'use client';

import { useState } from 'react';
import {
    buildClientRuntimeSupportPayload,
    type ClientRuntimeIncident,
} from '@/client/error-handling/client-runtime-incident';
import clsx from 'clsx';

const formatRevisionShort = (revision: string) => revision.slice(0, 12);

const resolveFailureTitle = (route: string): string => {
    if (route.startsWith('/a')) {
        return 'Не удалось загрузить административный раздел';
    }

    return 'Не удалось загрузить данные сервиса';
};

const resolveFailureHint = (incident: ClientRuntimeIncident): string => {
    if (incident.category === 'asset_loading') {
        return 'Похоже, часть клиентского кода обновилась и текущую вкладку нужно загрузить заново.';
    }

    if (incident.category === 'authentication' || incident.category === 'authorization') {
        return 'Проверьте активность текущей сессии и повторите загрузку страницы.';
    }

    if (incident.category === 'internal' || incident.category === 'dependency' || incident.category === 'request') {
        return 'Похоже, один из внутренних компонентов сейчас недоступен.';
    }

    return incident.retryable
        ? 'Похоже, один из внутренних компонентов сейчас недоступен.'
        : 'Текущую страницу не удалось безопасно отобразить.';
};

type RuntimeFailurePageProps = {
    incident: ClientRuntimeIncident;
    onRetry: () => void;
};

export const RuntimeFailurePage = ({ incident, onRetry }: RuntimeFailurePageProps) => {
    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
    const isRetryable = incident.retryable;
    const supportPayload = buildClientRuntimeSupportPayload(incident);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(supportPayload);
            setCopyState('copied');
        } catch {
            setCopyState('failed');
        }
    };

    return (
        <main className="min-h-screen bg-slate-100 px-4 py-10 md:px-6">
            <div className="mx-auto max-w-3xl space-y-4">
                <section
                    className={clsx(
                        'rounded-2xl border p-6 shadow-sm md:p-8',
                        isRetryable ? 'border-amber-200 bg-amber-50' : 'border-rose-200 bg-rose-50',
                    )}
                >
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <p
                                className={clsx(
                                    'text-xs font-semibold uppercase tracking-[0.2em]',
                                    isRetryable ? 'text-amber-700' : 'text-rose-700',
                                )}
                            >
                                {isRetryable ? 'Временная недоступность' : 'Требуется внимание'}
                            </p>
                            <h1 className="text-2xl font-semibold text-slate-950">
                                {resolveFailureTitle(incident.route)}
                            </h1>
                            <p className="text-sm leading-6 text-slate-700">{resolveFailureHint(incident)}</p>
                            <p className="text-sm leading-6 text-slate-700">
                                Последнее действие могло не завершиться. Не повторяйте массовые операции, пока страница
                                не восстановится.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={onRetry}
                                className={clsx(
                                    'rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition',
                                    isRetryable ? 'bg-amber-900 hover:bg-amber-950' : 'bg-rose-700 hover:bg-rose-800',
                                )}
                            >
                                Повторить
                            </button>
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
                            >
                                Обновить страницу
                            </button>
                            <button
                                type="button"
                                onClick={() => void handleCopy()}
                                className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
                            >
                                {copyState === 'copied'
                                    ? 'Скопировано'
                                    : copyState === 'failed'
                                      ? 'Не удалось скопировать'
                                      : 'Скопировать данные для поддержки'}
                            </button>
                        </div>
                    </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="space-y-4">
                        <div>
                            <h2 className="text-sm font-semibold text-slate-950">Диагностика</h2>
                            <p className="mt-1 text-xs leading-5 text-slate-600">
                                Этот блок не содержит токены и нужен для поддержки и поиска инцидента.
                            </p>
                        </div>

                        <dl className="grid gap-3 text-sm text-slate-700 md:grid-cols-2">
                            <div>
                                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">ID сбоя</dt>
                                <dd className="mt-1 break-all font-mono text-xs text-slate-900">
                                    {incident.failureId}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Маршрут</dt>
                                <dd className="mt-1 break-all text-slate-900">{incident.route}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Код</dt>
                                <dd className="mt-1 break-all font-mono text-xs text-slate-900">{incident.code}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Категория
                                </dt>
                                <dd className="mt-1 text-slate-900">{incident.category}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                    Можно повторить
                                </dt>
                                <dd className="mt-1 text-slate-900">{incident.retryable ? 'Да' : 'Нет'}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Версия</dt>
                                <dd className="mt-1 font-mono text-xs text-slate-900">
                                    {formatRevisionShort(incident.appRevision)}
                                </dd>
                            </div>
                            {incident.requestId && (
                                <div>
                                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                        Request ID
                                    </dt>
                                    <dd className="mt-1 break-all font-mono text-xs text-slate-900">
                                        {incident.requestId}
                                    </dd>
                                </div>
                            )}
                            {incident.correlationId && incident.correlationId !== incident.requestId && (
                                <div>
                                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                        Correlation ID
                                    </dt>
                                    <dd className="mt-1 break-all font-mono text-xs text-slate-900">
                                        {incident.correlationId}
                                    </dd>
                                </div>
                            )}
                            {incident.operationId && (
                                <div>
                                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                        Operation ID
                                    </dt>
                                    <dd className="mt-1 break-all font-mono text-xs text-slate-900">
                                        {incident.operationId}
                                    </dd>
                                </div>
                            )}
                            {incident.digest && (
                                <div>
                                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                        Digest
                                    </dt>
                                    <dd className="mt-1 break-all font-mono text-xs text-slate-900">
                                        {incident.digest}
                                    </dd>
                                </div>
                            )}
                        </dl>

                        <pre className="overflow-x-auto rounded-xl bg-slate-950 p-4 text-[11px] leading-5 text-slate-100">
                            {supportPayload}
                        </pre>
                    </div>
                </section>
            </div>
        </main>
    );
};
